import {
  Box,
  Button,
  Checkbox,
  CollectionPreferences,
  Header,
  Pagination,
  PropertyFilter,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-core';
import React, { useContext, useEffect, useCallback, useMemo, useState, type JSX } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { deepClone, tablePaginationAriaLabels, valueExist } from '../../api/UtilsApi';
import constants from '../../common/constants';
import LocalStorage from '../../common/LocalStorage';
import {
  ContentWorkflowTableItem,
  DefaultTablePageSizeOptions,
  TablePreferenceCheckboxType,
  getDefaultTablePreferenceCache,
} from '../workflow-viewer/ContentWorkflowTableDefinitions';
import {
  applyCustomPropertyFilter,
  buildTableFilterProperties,
  buildTableFilterPropertiesMap,
  DefaultCustomGroupsText,
  DefaultI18nStrings,
  freeTextProperties,
  getDefaultCachedQuery,
  getFilterCounterText,
  setDefaultCachedQuery,
} from './ContentWorkflowTableFilters';
import { TableEmptyState, TableNoMatchState } from '../common/CommonComponents';
import CsvDownloadButton from '../comment/CsvDownloadButton';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { DateTime } from 'luxon';
import CustomFilterActionControls from './CustomFilterActionControls';
import { AppContext } from '../../contexts/AppContext';
import { TableProps } from '@amzn/awsui-components-core/table/interfaces';

interface PaginatedTableProps {
  actionControls?: JSX.Element;
  columnDefinitions: any[];
  csvFormat: Array<{ header: string; value: (i: ContentWorkflowTableItem) => any }>;
  csvFileName: string;
  defaultQuery?: any;
  header?: string;
  headerDescription?: string;
  items: ContentWorkflowTableItem[];
  loading?: boolean;
  selectedItems: ContentWorkflowTableItem[];
  showCustomFilters?: boolean;
  onSelectionChange?: (items: ContentWorkflowTableItem[]) => void;
  tableSelectorOptions: any[];
  tableId: string;
  tableDataProvider?: JSX.Element;
  queryTimestamp: DateTime;
  queryErrorInfo?: JSX.Element;
  sortingOption?: any;
  stickyColumns?: TableProps.StickyColumns;
}

const blankQuery: any = {
  tokens: [],
  operation: 'and',
};

const PaginatedTableV3: React.FunctionComponent<PaginatedTableProps> = (
  props: PaginatedTableProps,
) => {
  const {
    actionControls,
    csvFormat,
    csvFileName,
    columnDefinitions,
    defaultQuery,
    header,
    headerDescription,
    loading,
    selectedItems,
    showCustomFilters,
    onSelectionChange,
    tableId,
    tableSelectorOptions,
  } = props;

  const [tablePreferenceCache, setTablePreferenceCache] = useState(
    getDefaultTablePreferenceCache(tableId, tableSelectorOptions),
  );
  const [tablePreferences, setTablePreferences] = useState(deepClone(tablePreferenceCache));
  const [contentDescriptionGroup, setContentDescriptionGroup] = useState([
    {
      label: 'Attributes',
      options: tableSelectorOptions.filter((option) => !option.hidePreference),
    },
  ]);
  const [query, setQuery] = useState(getDefaultCachedQuery(tableId, defaultQuery || blankQuery));
  const { clearFiltersOnRefresh, pageSize, stickyHeader, visibleColumns, wrapLines } =
    tablePreferences;
  const flashbarMessageBroker: FlashbarMessageBroker = useMemo(
    () => FlashbarMessageBroker.create(),
    [],
  );
  const appContext = useContext(AppContext);
  const entities = appContext?.entities || {};

  useEffect(() => {
    if (tablePreferenceCache && tablePreferenceCache.visibleColumns?.size > 0) {
      const updatedSelectorOptions: any = deepClone(tableSelectorOptions);
      updatedSelectorOptions.forEach(
        // @ts-expect-error TS7006 - strict migration
        (c) => (c.visible = tablePreferenceCache.visibleColumns.has(c.id)),
      );
      setContentDescriptionGroup([
        {
          label: 'Attributes',
          // @ts-expect-error TS7006 - strict migration
          options: updatedSelectorOptions.filter((option) => !option.hidePreference),
        },
      ]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setQuery(getDefaultCachedQuery(tableId, defaultQuery || blankQuery));
  }, [defaultQuery, tableId]);

  useEffect(() => {
    if (clearFiltersOnRefresh) {
      onClearFilter();
    }
  }, [props.queryTimestamp]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteringProperties = useMemo(
    () => buildTableFilterProperties(columnDefinitions),
    [columnDefinitions],
  );
  const filteringPropertiesMap = useMemo(
    () => buildTableFilterPropertiesMap(filteringProperties),
    [filteringProperties],
  );

  const onClearFilter = () => {
    actions.setPropertyFiltering(blankQuery);
  };

  // @ts-expect-error TS7006 - strict migration
  const onQueryChange = (changedQuery) => {
    actions.setPropertyFiltering(changedQuery);
  };

  const onSaveFilters = () => {
    setDefaultCachedQuery(tableId, propertyFilterProps.query);
    flashbarMessageBroker.pushSuccessMessage(constants.SEARCH_FILTERS_SAVE_SUCCESS_TEXT);
  };

  const handleClearPreferences = () => {
    LocalStorage.removeTableSettingOptions(tableId);
    const defaultTablePreferenceCache = getDefaultTablePreferenceCache(
      tableId,
      tableSelectorOptions,
    );
    setTablePreferenceCache(defaultTablePreferenceCache);
    setTablePreferences(deepClone(defaultTablePreferenceCache));
    setContentDescriptionGroup([
      {
        label: 'Attributes',
        options: tableSelectorOptions.filter((option) => !option.hidePreference),
      },
    ]);
  };

  // @ts-expect-error TS7006 - strict migration
  const updateTablePreferences = (updatedTablePreferenceCache) => {
    LocalStorage.setTableSettingOptions(tableId, updatedTablePreferenceCache);
    setTablePreferenceCache(updatedTablePreferenceCache);
    setTablePreferences(deepClone(updatedTablePreferenceCache));
  };

  const changeCheckboxSetting = (e: any, checkboxId: any) => {
    const { checked } = e.detail;
    const updatedTablePreferenceCache = tablePreferenceCache;
    if (valueExist(checked)) {
      if (checkboxId === TablePreferenceCheckboxType.StickyHeader) {
        updatedTablePreferenceCache.stickyHeader = checked;
      } else if (checkboxId === TablePreferenceCheckboxType.ClearFiltersOnRefresh) {
        updatedTablePreferenceCache.clearFiltersOnRefresh = checked;
      }
    }
    updateTablePreferences(updatedTablePreferenceCache);
  };

  const changeTableSetting = (e: any) => {
    const { pageSize: size, wrapLines: wrapLinesFlag, visibleContent } = e.detail;
    const updatedTablePreferenceCache = tablePreferenceCache;
    if (valueExist(visibleContent)) {
      updatedTablePreferenceCache.visibleColumns = visibleContent;
    }
    if (valueExist(size)) {
      updatedTablePreferenceCache.pageSize = size;
    }
    if (valueExist(wrapLinesFlag)) {
      updatedTablePreferenceCache.wrapLines = wrapLinesFlag;
    }
    updateTablePreferences(updatedTablePreferenceCache);
  };

  const headerComp = (
    <Header
      variant="h3"
      counter={`(${props.items.length})`}
      description={headerDescription}
      actions={actionControls}
    >
      {`${header || 'Total'} `}
    </Header>
  );

  const preferences = {
    clearFiltersOnRefresh,
    pageSize,
    visibleContent: [...visibleColumns],
    wrapLines,
    stickyHeader,
  };

  const visibleContentPreference = {
    title: constants.TABLE_CONTENT_SELECTOR_TITLE,
    options: contentDescriptionGroup,
  };

  const customPreference = (value: any, setValue: React.Dispatch<any>) => {
    return (
      <div>
        <div className="padding-bottom-10">
          <Checkbox
            onChange={(e) => changeCheckboxSetting(e, TablePreferenceCheckboxType.StickyHeader)}
            checked={!!stickyHeader}
            description={constants.STICKY_HEADER_TOOLTIP}
          >
            Sticky header
          </Checkbox>
        </div>
        <div className="padding-bottom-10">
          <Checkbox
            onChange={(e) =>
              changeCheckboxSetting(e, TablePreferenceCheckboxType.ClearFiltersOnRefresh)
            }
            checked={!!clearFiltersOnRefresh}
            description={constants.CLEAR_FILTERS_REFRESH_TOOLTIP}
          >
            Clear filters on refresh
          </Checkbox>
        </div>
        <CsvDownloadButton data={props.items} format={csvFormat} filename={csvFileName} />
      </div>
    );
  };

  const {
    items,
    actions,
    collectionProps,
    filteredItemsCount,
    paginationProps,
    propertyFilterProps,
  } = useCollection(props.items, {
    propertyFiltering: {
      defaultQuery: query,
      filteringProperties,
      empty: <TableEmptyState />,
      noMatch: <TableNoMatchState onClearFilter={onClearFilter} />,
      filteringFunction: (item, userQuery) => {
        return applyCustomPropertyFilter(
          item,
          userQuery,
          filteringPropertiesMap,
          freeTextProperties,
          entities,
        );
      },
    },
    filtering: {
      empty: 'No contents',
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: props.sortingOption ?? {},
    selection: {},
  });

  // Workaround to get all filtered items
  const filteringCollection = useCollection(props.items, {
    pagination: { pageSize: Number.MAX_SAFE_INTEGER },
    propertyFiltering: {
      filteringProperties,
      filteringFunction: (item, userQuery) => {
        return applyCustomPropertyFilter(
          item,
          userQuery,
          filteringPropertiesMap,
          freeTextProperties,
          entities,
        );
      },
    },
  });
  const updatedQuery = propertyFilterProps.query;
  useEffect(() => {
    filteringCollection.propertyFilterProps.onChange({ detail: updatedQuery });
  }, [updatedQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const collectionPreferences = (
    <div>
      <Box display="inline-block">
        <CollectionPreferences
          title={constants.PREFERENCES}
          confirmLabel={constants.CONFIRM_BUTTON_TEXT}
          cancelLabel={constants.CANCEL_BUTTON_TEXT}
          preferences={preferences}
          onConfirm={changeTableSetting}
          pageSizePreference={{
            title: constants.TABLE_PAGE_SELECTOR_MESSAGE,
            options: DefaultTablePageSizeOptions,
          }}
          wrapLinesPreference={{
            label: constants.TABLE_WRAPLINES_LABEL,
            description: constants.TABLE_WRAPLINES_DESCRIPTION,
          }}
          visibleContentPreference={visibleContentPreference}
          customPreference={customPreference}
        />
      </Box>
      <Box display="inline-block">
        <ModalDialogButtonV3
          iconName="status-stopped"
          header={constants.CLEAR_PREFERENCES_TEXT}
          text={constants.CLEAR_PREFERENCES_CONFIRM_TEXT}
          submitButtonText={constants.CONFIRM_BUTTON_TEXT}
          onSubmit={handleClearPreferences}
          variant="icon"
        />
      </Box>
      <Box display="inline-block">
        <CsvDownloadButton
          data={filteringCollection.items}
          format={csvFormat}
          filename={csvFileName}
          renderInline
        />
      </Box>
    </div>
  );

  const filters = (
    <SpaceBetween size={'s'}>
      {props.tableDataProvider}
      <div>
        <Box display="inline-block">
          <PropertyFilter
            i18nStrings={DefaultI18nStrings}
            {...propertyFilterProps}
            countText={getFilterCounterText(filteredItemsCount || 0)}
            customGroupsText={DefaultCustomGroupsText}
          />
        </Box>
        <Box display="inline-block" margin={{ left: 's' }}>
          {showCustomFilters ? (
            <CustomFilterActionControls
              tableId={tableId}
              query={propertyFilterProps.query}
              onQueryChange={onQueryChange}
            />
          ) : (
            <Button onClick={onSaveFilters} variant="normal">
              {constants.SAVE_BUTTON_TEXT}
            </Button>
          )}
        </Box>
      </div>
    </SpaceBetween>
  );

  if (props.queryErrorInfo) {
    collectionProps.empty = props.queryErrorInfo;
  }

  return (
    <Table
      {...collectionProps}
      filter={filters}
      loading={!!loading}
      header={headerComp}
      columnDefinitions={columnDefinitions}
      items={items}
      wrapLines={!!wrapLines}
      resizableColumns
      selectionType="multi"
      stickyHeader={!!stickyHeader}
      stickyHeaderVerticalOffset={0}
      pagination={<Pagination {...paginationProps} ariaLabels={tablePaginationAriaLabels} />}
      preferences={collectionPreferences}
      visibleColumns={[...visibleColumns]}
      selectedItems={selectedItems}
      // @ts-expect-error TS2722 - strict migration
      onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
      ariaLabels={{ tableLabel: 'Content table' }}
      stickyColumns={props.stickyColumns}
    />
  );
};

export default PaginatedTableV3;
